import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import Map from "../../components/Map";
import Search from "../../components/Search";
import UserBadge from "../../components/buttons/UserBadge";
import BackButton from "../../components/buttons/BackButton";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import it from "date-fns/locale/it";
import iconPlus from "../../assets/icon/general/piu.png";
import ImageUploadCard from "../../components/ImageUploadCard";
import OperatorMentionInput from "../../components/OperatorMetionInput";
import api from "../../Api";
import ActivitySelector from "./CustomMultiSelect";
import MeansSelector from "./MeansSelector";

registerLocale("it", it);

export default function NewActivity() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [mapPosition, setMapPosition] = useState(null);
  const [formData, setFormData] = useState({
    activity_names: [],
    means: [],
    priority: "Media",
    operator_id: "",
    description: "",
    latitude: "",
    longitude: "",
    location_info: "",
    status: "pending",
    assigned_user_id: "",
  });
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  // ORA photoBefore e photoAfter sono ARRAY di File (anche vuoti)
  const [photoBefore, setPhotoBefore] = useState([]); 
  const [photoAfter, setPhotoAfter] = useState([]);

  useEffect(() => {
    if (user && user.id) {
      setFormData((prev) => ({ ...prev, assigned_user_id: user.id }));
    }
  }, [user]);

  const handleSearch = async (query) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
      );
      const results = await response.json();
      if (results.length > 0) {
        const first = results[0];
        setMapPosition({
          lat: parseFloat(first.lat),
          lng: parseFloat(first.lon),
        });
      }
    } catch (error) {
      console.error("Errore durante la ricerca:", error);
    }
  };

  useEffect(() => {
    if (mapPosition) {
      setFormData((prev) => ({
        ...prev,
        latitude: mapPosition.lat,
        longitude: mapPosition.lng,
      }));
    }
  }, [mapPosition]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = [
      "activity_names",
      "means",
      "priority",
      "operator_id",
      "latitude",
      "longitude",
      "location_info",
      "assigned_user_id",
    ];

    const missing = required.filter(
      (field) =>
        !formData[field] ||
        (Array.isArray(formData[field]) && formData[field].length === 0)
    );
    if (missing.length > 0 || !startDateTime || !endDateTime) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }

    const payload = new FormData();

    // Aggiungo ogni activity_name singolarmente
    formData.activity_names.forEach((name) => {
      payload.append("activity_names", name);
    });

    // Aggiungo ogni mezzo singolarmente
    formData.means.forEach((m) => {
      payload.append("means", m);
    });

    payload.append("priority", formData.priority);
    payload.append("operator_id", formData.operator_id);
    payload.append("description", formData.description);
    payload.append("start_date", startDateTime.toISOString());
    payload.append("end_date", endDateTime.toISOString());
    payload.append("latitude", formData.latitude);
    payload.append("longitude", formData.longitude);
    payload.append("location_info", formData.location_info);
    payload.append("status", formData.status);
    payload.append("assigned_user_id", formData.assigned_user_id);

    // Se ci sono file “Prima”, li appendo uno a uno
    if (photoBefore.length > 0) {
      photoBefore.forEach((file) => {
        payload.append("photo_before", file);
      });
    }

    // Se ci sono file “Dopo”, li appendo uno a uno
    if (photoAfter.length > 0) {
      photoAfter.forEach((file) => {
        payload.append("photo_after", file);
      });
    }

    try {
      await api.post("/activity/activities", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attività creata con successo!");
      setTimeout(() => navigate("/"), 2000);
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Errore durante la creazione dell'attività.");
    }
  };

  return (
    <div className="min-h-screen bg-global p-4">
      <header className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <Search onSearch={handleSearch} />
        </div>
        <div className="flex space-x-2 items-center">
          <UserBadge />
          <BackButton />
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="max-w-[800px] max-h-[400px] overflow-hidden rounded-2xl">
            <Map
              position={mapPosition}
              setPosition={setMapPosition}
              onLocationInfo={(info) =>
                setFormData((prev) => ({
                  ...prev,
                  location_info: info.display_name,
                }))
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-6">
            {/* ImageUploadCard per “Prima”: riceve un array di File tramite onFileSelect */}
            <ImageUploadCard
              label='Carica foto "Prima"'
              name="photo_before"
              icon={iconPlus}
              previewImages={[]} // nessuna immagine esistente su server (fase di creazione)
              onFileSelect={(filesArray) => {
                setPhotoBefore(filesArray);
              }}
              disabled={false}
              onRemove={null} // non ci sono immagini server da rimuovere
            />

            {/* ImageUploadCard per “Dopo”: stesso principio */}
            <ImageUploadCard
              label='Carica foto "Dopo"'
              name="photo_after"
              icon={iconPlus}
              previewImages={[]}
              onFileSelect={(filesArray) => {
                setPhotoAfter(filesArray);
              }}
              disabled={false}
              onRemove={null}
            />
          </div>
        </div>

        <div className="bg-primary rounded-2xl shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-6">
                <ActivitySelector
                  value={formData.activity_names}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      activity_names: selected,
                    }))
                  }
                />
                <MeansSelector
                  value={formData.means}
                  onChange={(selected) =>
                    setFormData((prev) => ({ ...prev, means: selected }))
                  }
                />
                <div>
                  <label className="block font-medium text-green-500 mb-2">
                    Priorità
                  </label>
                  <div className="flex flex-col gap-2 text-white">
                    {["Bassa", "Media", "Alta"].map((level) => (
                      <label key={level} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="priority"
                          value={level}
                          checked={formData.priority === level}
                          onChange={handleInputChange}
                          className="appearance-none border-2 border-green-500 bg-primary checked:bg-green-500 rounded-full w-4 h-4 focus:outline-none"
                        />
                        {level}
                      </label>
                    ))}
                  </div>
                </div>
                <OperatorMentionInput
                  value={formData.operator_id}
                  onChange={(val) =>
                    setFormData((prev) => ({ ...prev, operator_id: val }))
                  }
                />
                <div>
                  <label className="block font-medium text-green-500">
                    Indirizzo
                  </label>
                  {formData.location_info ? (
                    (() => {
                      const parts = formData.location_info
                        .split(",")
                        .map((p) => p.trim());
                      const civico = parts[0] || "";
                      const via = parts[1] || "";
                      const città = parts[4] || "";
                      return (
                        <div className="text-white text-sm mt-2 ml-1">
                          📍 {via} {civico}, {città}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-white text-sm mt-2 ml-1">
                      Nessuna via cercata
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-8">
                <div className="w-full flex justify-end">
                  <button
                    type="submit"
                    className="bg-primary border-2 border-green-600 rounded-full text-white p-2 px-4 hover:border-green-700 hover:bg-secondary"
                  >
                    Crea attività
                  </button>
                </div>

                <div>
                  <label className="flex justify-start ml-2 font-medium text-green-500 mb-4 mt-14">
                    Scegli una data
                  </label>
                  <div className="flex gap-12 justify-center mb-2">
                    <div className="flex flex-col items-center text-white">
                      <span>
                        {(startDateTime || new Date("0000-01-01")).toLocaleDateString(
                          "it-IT",
                          {
                            day: "2-digit",
                            month: "2-digit",
                          }
                        )}
                      </span>
                      <small>inizio</small>
                    </div>
                    <div className="flex flex-col items-center text-white">
                      <span>
                        {(endDateTime || new Date("0000-01-01")).toLocaleDateString(
                          "it-IT",
                          {
                            day: "2-digit",
                            month: "2-digit",
                          }
                        )}
                      </span>
                      <small>fine</small>
                    </div>
                    <div className="flex flex-col items-center text-white">
                      <span>
                        {startDateTime ? startDateTime.getFullYear() : "0000"}
                      </span>
                      <small>anno</small>
                    </div>
                  </div>
                  <DatePicker
                    inline
                    selectsRange
                    startDate={startDateTime}
                    endDate={endDateTime}
                    onChange={(update) => {
                      setStartDateTime(update[0]);
                      setEndDateTime(update[1]);
                    }}
                    dateFormat="dd/MM/yyyy"
                    locale="it"
                    calendarClassName="bg-primary w-full flex justify-center border-none"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block font-medium text-green-500">Descrizione</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full border p-2 rounded bg-primary border-secondary focus:outline-none focus:ring-2 focus:ring-secondary text-white min-h-[250px]"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
